import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import ExpandImageModal from '../modals/expandImage/ExpandImageModal';
import ThumbnailUtil from '../util/ThumbnailUtil';
import DrawingCanvas from './DrawingCanvas';
import { findDOMNode } from 'react-dom';
import screenfull from 'screenfull';
import PreviewHelper from '../helper/PreviewHelper';
import { KeyCode, PdfPreviewOption, Visibility } from '../util/Constants';
import AnnotationUtil from '../queries/annotations/AnnotationUtil';
import Div from '../common/elements/Div';
import Row from '../common/layout/Row';
import Center from '../common/layout/Center';
import MultipageControls from '../common/MultipageControls';
import ViewUtil from '../ViewUtil';
import { useNewImagePreview } from '../util/FeatureUtil';
import SmartRangeInput from '../common/SmartRangeInput';
import ScaleableAssetView, { ScaleableAssetViewWithComments, AIData } from '../assets/ScaleableAssetView';
import Spinner from '../common/Spinner';
import Store from '../store/Store';
import {
  MULTIPAGE_ASSET_CURRENT_PAGE,
  ASSET_PREVIEW_INFO,
  ASSET_SCALE_INFO,
  SHOW_COMMENTS,
  ACTIVE_COMMENT_ID
} from '../store/Constants';
import StoreListener from '../store/StoreListener';
import { toggleFullscreenClass } from '../util/DOMUtil';
import FetchJsonJob from '../protocol/FetchJsonJob';
import t from '../i18n/I18n';
import { ThemeSetup } from '../themes/ThemeSetup';

import './AssetImagePreview.css';
import { isMobile } from '../util/ScreenUtil';
import PreviewController from '../controllers/preview/PreviewController';
import Storage from '../protocol/Storage';
import { RepoItem } from '../autogenerated/ZoomBridge';
import HashMap from '../protocol/HashMap';

const _store = {
  fuid: null,
  currentPage: null
};

const storeItems = [
  { name: MULTIPAGE_ASSET_CURRENT_PAGE, defaultValue: 1 },
  { name: SHOW_COMMENTS, defaultValue: true },
  { name: ASSET_SCALE_INFO, defaultValue: {} }
];

@StoreListener(storeItems)
export default class AssetImagePreview extends React.Component {
  static propTypes = {
    collection: PropTypes.object,
    asset: PropTypes.object,
    selectedAsset: PropTypes.object,
    isMultiPage: PropTypes.bool,
    allowFullscreen: PropTypes.bool,
    onFullScreenToggle: PropTypes.func,
    editMode: PropTypes.bool,
    pdfPreviewOption: PropTypes.string,
    onPageChange: PropTypes.func,
    shapeDrawingStart: PropTypes.func,
    shapeDrawingStop: PropTypes.func,
    onTextInputHide: PropTypes.func,
    onRender: PropTypes.func,
    onClickThroughShape: PropTypes.func,
    enableComments: PropTypes.bool,
    addingShape: PropTypes.object,
    onShapeCreated: PropTypes.func,
    onShapeDeleted: PropTypes.func,
    onShapeDrag: PropTypes.func,
    onShapeDragEnd: PropTypes.func,
    isExpandedMode: PropTypes.bool,
    getFullScreenNode: PropTypes.func,
    onFullScreenChange: PropTypes.func,
    addMouseListener: PropTypes.func,
    removeMouseListener: PropTypes.func,
    onCropResizeStop: PropTypes.func,
    comments: PropTypes.array,
    saveReply: PropTypes.func,
    deleteReply: PropTypes.func,
    activeCommentIdInPanel: PropTypes.number,
    onScaleChange: PropTypes.func,
    cropMode: PropTypes.bool,
    renderDownloadButton: PropTypes.func,
    [MULTIPAGE_ASSET_CURRENT_PAGE]: PropTypes.number,
    [ASSET_SCALE_INFO]: PropTypes.object,
    metadataVisibiity: PropTypes.object,
    isAIData: PropTypes.any,
    isCompareMode: PropTypes.bool,
    isFolder: PropTypes.bool
  };

  static defaultProps = {
    allowFullscreen: true,
    onPageChange: _.noop,
    isExpandedMode: false,
    enableComments: false,
    onFullScreenChange: _.noop,
    addMouseListener: _.noop,
    removeMouseListener: _.noop,
    renderDownloadButton: _.noop,
    isAIData: true
  };

  static contextTypes = {
    getTopBarVisibility: PropTypes.func,
    setTopBarVisibility: PropTypes.func
  };

  _loadInfoJob: FetchJsonJob;

  constructor(props) {
    super(props);
    this._refocusInput = false;
    this._listeningForArrowKeys = false;

    this.state = {
      selectedAsset: this.props.selectedAsset,
      imageNormalFits: true,
      imageLoading: true,
      imageDataReady: false,
      imageSrc: ThumbnailUtil.createImagePreviewUrl(this.props.selectedAsset, this.currentPage),
      imageUnavailable: false,
      imagePreviewResolutionX: 0,
      imagePreviewResolutionY: 0,
      numPages: 0,
      expandImageModal: {
        show: false
      },
      // This is just the value in the input field. Not to be confused with this.currentPage.
      currentPage: 1,
      controlFocus: false,

      useNewImagePreview: useNewImagePreview()
    };

    const openOnPage = _store.fuid === props.selectedAsset._fuid ? _store.currentPage : 1;
    this.updatePage(openOnPage);
    Store.put(MULTIPAGE_ASSET_CURRENT_PAGE, openOnPage);
    this.handleImageLoad = this.handleImageLoad.bind(this);
    this.handleImageError = this.handleImageError.bind(this);
    this.handleExpandClick = this.handleExpandClick.bind(this);
    this.renderScreenToggleButton = this.renderScreenToggleButton.bind(this);

    this.checkedTheAuthError = false;
  }

  onResize = () => {
    if (this.refs.imageContainer) {
      let imageNormalFits = this.checkIfImageFits();
      this.setState({
        imageNormalFits
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    if (!window.gs.isReviewServerLogged) {
      let isAuthSuccess = await PreviewController.checkPreviewAuthentication();
      if (isAuthSuccess === false) {
        isAuthSuccess = await PreviewController.issuePreviewServerAuth(true);
      }
    }

    const scope = this;
    // window.addEventListener('resize', (event) => {
    //   if (this.refs.imageContainer) {
    //     let imageNormalFits = scope.checkIfImageFits();
    //     scope.setState({
    //       imageNormalFits
    //     });
    //   }
    // });
    window.addEventListener('resize', this.onResize);
    if (screenfull.enabled) {
      screenfull.on('change', () => {
        toggleFullscreenClass();
        if (screenfull.isFullscreen) {
          this.props.addMouseListener();
        } else {
          this.props.removeMouseListener();
        }

        this.props.onFullScreenChange(screenfull.isFullscreen);
      });
    }

    if (this.props.isMultiPage && this.props.pdfPreviewOption === PdfPreviewOption.PAGE_BY_PAGE) {
      this.addArrowKeyListeners();
    }
  }

  componentWillMount() {
    this.loadAssetInfo();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.selectedAsset !== this.state.selectedAsset) {
      this.setState({ selectedAsset: nextProps.selectedAsset });
    }
    if (nextProps[MULTIPAGE_ASSET_CURRENT_PAGE] !== this.props[MULTIPAGE_ASSET_CURRENT_PAGE]) {
      this.updatePage(nextProps[MULTIPAGE_ASSET_CURRENT_PAGE], true);
    }
  }

  componentWillUpdate(nextProps) {
    const { drawingCanvas } = this.refs;
    if (
      this.props.pdfPreviewOption === PdfPreviewOption.PAGE_BY_PAGE &&
      nextProps.pdfPreviewOption === PdfPreviewOption.CONTINUOUS &&
      drawingCanvas
    ) {
      drawingCanvas.recalculateScrollTop();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    screenfull.off('change');
    if (this.props.isMultiPage) {
      this.removeArrowKeyListeners();
      _store.fuid = this.props.selectedAsset._fuid;
      _store.currentPage = this.currentPage;
    } else {
      _store.fuid = null;
      _store.currentPage = null;
    }

    if (this._loadInfoJob && !this._loadInfoJob.done) {
      this._loadInfoJob.cancel();
    }
    Store.put(ACTIVE_COMMENT_ID, -1);
    // Storage.set('BACK_TO', null);

    window.removeEventListener('resize', this.onResize);
  }

  componentDidUpdate(prevProps, prevState) {
    const { selectedAsset } = prevState;
    if (selectedAsset.getFuid() !== this.state.selectedAsset.getFuid()) {
      const assetMetadataMap = this.props.selectedAsset.getMetadataMap();
      this.loadAssetInfo();
      Store.put(MULTIPAGE_ASSET_CURRENT_PAGE, 1);
      this.setState({
        imageNormalFits: this.checkIfImageFits(),
        imageUnavailable: false,
        imageLoading: true,
        imageDataReady: false
      });
      //this.refs.drawingCanvas.setState({imageSrc: ThumbnailUtil.createImagePreviewUrl(this.props.selectedAsset, this.currentPage)});
      const { drawingCanvas } = this.refs;
      if (drawingCanvas) {
        drawingCanvas.setState({ imageSrc: ThumbnailUtil.createImagePreviewUrl(this.props.selectedAsset) });
      }
    }

    if (this._input && this._refocusInput) {
      this._refocusInput = false;
      this._input.focus();
    }

    if (
      this.props.isMultiPage &&
      this.props.pdfPreviewOption === PdfPreviewOption.PAGE_BY_PAGE &&
      !this._listeningForArrowKeys
    ) {
      this.addArrowKeyListeners();
    } else if (
      (!this.props.isMultiPage || this.props.pdfPreviewOption === PdfPreviewOption.CONTINUOUS) &&
      this._listeningForArrowKeys
    ) {
      this.removeArrowKeyListeners();
    }

    if (this.state.imageDataReady && this.executeAfterReady) {
      this.executeAfterReady();
      this.executeAfterReady = null;
    }
  }

  setStateOnMountCheck(state) {
    if (this._isMounted) {
      this.setState(state);
    }
  }

  addArrowKeyListeners = () => {
    document.addEventListener('keyup', this.onArrowKeyUp);
    this._listeningForArrowKeys = true;
  };

  removeArrowKeyListeners = () => {
    document.removeEventListener('keyup', this.onArrowKeyUp);
    this._listeningForArrowKeys = false;
  };

  toggleCrop() {
    const { drawingCanvas } = this.refs;
    if (drawingCanvas) {
      drawingCanvas.toggleCrop();
    }
  }

  getCropSelection() {
    const { drawingCanvas } = this.refs;
    if (drawingCanvas) {
      drawingCanvas.getCropSelection();
    }
  }

  isCropMode() {
    const { drawingCanvas } = this.refs;
    if (drawingCanvas) {
      drawingCanvas.state.cropMode;
    }
  }

  toggleTextInput() {
    const { drawingCanvas } = this.refs;
    if (drawingCanvas) {
      drawingCanvas.toggleTextInput();
    }
  }

  checkIfImageFits(
    imagePreviewResolutionX = this.state.imagePreviewResolutionX,
    imagePreviewResolutionY = this.state.imagePreviewResolutionY
  ) {
    if (!this.refs.wrapper) {
      return false;
    }

    let imageNormalFits = true;
    // if (!this.state.imagePreviewResolutionX || !this.state.imagePreviewResolutionY || (this.state.imagePreviewResolutionX > this.refs.wrapper.offsetWidth
    //   || this.state.imagePreviewResolutionY > this.refs.wrapper.offsetHeight)) {
    //   imageNormalFits = false;
    // }
    if (
      imagePreviewResolutionX > this.refs.wrapper.offsetWidth ||
      imagePreviewResolutionY > this.refs.wrapper.offsetHeight
    ) {
      imageNormalFits = false;
    }
    return imageNormalFits;
  }

  async handleImageError(error) {
    if (!this.checkedTheAuthError) {
      this.checkedTheAuthError = true;
      let isAuthSuccess = await PreviewController.checkPreviewAuthentication();
      if (isAuthSuccess === false) {
        isAuthSuccess = await PreviewController.issuePreviewServerAuth(true);
        if (isAuthSuccess) {
          this.forceUpdate();
          return;
        }
      }
    }

    //ErrorHandler.handleError(error);
    console.error('Error in loading the image info: ', this.state.selectedAsset, error);
    this.setPreviewErrorImage();
  }

  handleImageLoad() {
    this.setState({
      imageLoading: false
    });
  }

  addEscapeListener = () => {
    document.addEventListener('fullscreenchange', this.onExitFullScreenClickForEsc);
    document.addEventListener('webkitfullscreenchange', this.onExitFullScreenClickForEsc);
    document.addEventListener('mozfullscreenchange', this.onExitFullScreenClickForEsc);
    document.addEventListener('MSFullscreenChange', this.onExitFullScreenClickForEsc);
  };

  removeEscapeListener = () => {
    document.removeEventListener('fullscreenchange', this.onExitFullScreenClickForEsc);
    document.removeEventListener('webkitfullscreenchange', this.onExitFullScreenClickForEsc);
    document.removeEventListener('mozfullscreenchange', this.onExitFullScreenClickForEsc);
    document.removeEventListener('MSFullscreenChange', this.onExitFullScreenClickForEsc);
  };

  onExitFullScreenClickForEsc = () => {
    if (
      !document.fullscreenElement &&
      !document.webkitIsFullScreen &&
      !document.mozFullScreen &&
      !document.msFullscreenElement
    ) {
      this.context.setTopBarVisibility(Visibility.VISIBLE);
      this.removeEscapeListener();
      if (screenfull.isFullscreen) {
        screenfull.exit();
      }
    }
  };

  onExitFullScreenClick = async() => {
    this.context.setTopBarVisibility(Visibility.VISIBLE);
    this.removeEscapeListener();
    if (screenfull.isFullscreen) {
      await screenfull.exit();
      window.render()
    }
  };

  handleExpandClick = () => {
    if (ViewUtil.isDirectLinkToCollection()) {
      this.context.setTopBarVisibility(Visibility.HIDDEN);
      this.addEscapeListener();
    } else if (this.props.onFullScreenToggle) {
      this.props.onFullScreenToggle();
    } else {
      this.addEscapeListener();
      this.viewTofullScreen();
      if (isMobile()) {
        setTimeout(() => {
          this.viewTofullScreen();
        }, 500);
      }
    }
  };

   viewTofullScreen = async() => {
    const { getFullScreenNode } = this.props;
    const node = getFullScreenNode ? getFullScreenNode() : findDOMNode(this.refs.wrapper);
    this.context.setTopBarVisibility(Visibility.HIDDEN);
    await screenfull.request(node);
    window.render();
  };

  onPageChange = (e) => {
    const val = e.target.value.trim();
    if (val === '') {
      return this.setState({ currentPage: val });
    }

    const parsedVal = parseInt(val, 10);
    if (/^\d+$/.test(val) && !isNaN(parsedVal)) {
      this.setState({ currentPage: parsedVal });
    }
  };

  onFocus = (e) => {
    if (e.target.setSelectionRange) {
      const len = e.target.value.length;
      e.target.setSelectionRange(len, len);
    }

    this.setState({ controlFocus: true });
  };

  onBlur = () => {
    this.setState({ controlFocus: false });
  };

  onKeyUp = (e) => {
    if (e.keyCode !== KeyCode.ENTER) {
      return;
    }

    const parsedVal = parseInt(this.state.currentPage, 10);
    if (isNaN(parsedVal)) {
      return;
    }

    if (parsedVal < 1 || parsedVal > this.state.numPages) {
      return;
    }

    this._refocusInput = true;
    this.updatePage(parsedVal, true);
  };

  onArrowKeyUp = (e) => {
    const { keyCode } = e;
    if (!_.includes([KeyCode.LEFT, KeyCode.RIGHT], keyCode)) {
      return;
    }

    const targetNodes = [document.body, document.querySelector('.DefaultAssetView')];
    if (!_.includes(targetNodes, e.target) || this.props.editMode) {
      return;
    }

    let parsedVal = parseInt(this.state.currentPage, 10);
    if (isNaN(parsedVal)) {
      return;
    }

    parsedVal += keyCode === KeyCode.LEFT ? -1 : 1;
    if (parsedVal < 1 || parsedVal > this.state.numPages) {
      return;
    }

    this.updatePage(parsedVal, true);
  };

  updatePage = (page, recalculateScrollTop = false) => {
    if (page === this.currentPage || page < 1 || page > this.state.numPages) {
      if (!this.state.imageDataReady) {
        this.executeAfterReady = () => this.updatePage(this.props[MULTIPAGE_ASSET_CURRENT_PAGE], true);
      }
      return;
    }

    this.currentPage = page;
    Store.put(MULTIPAGE_ASSET_CURRENT_PAGE, page);

    if (recalculateScrollTop && this.refs.drawingCanvas) {
      this.refs.drawingCanvas.recalculateScrollTop();
    }

    this.setState({
      imageSrc: ThumbnailUtil.createImagePreviewUrl(this.props.selectedAsset, this.currentPage),
      imageLoading: true,
      currentPage: page
    });

    this.props.onPageChange(page);
  };

  displayNoPreviewAvailable() {
    setTimeout(() => {
      if (this.refs.imageContainer) this.refs.imageContainer.style['display'] = 'inline-block';
    }, 200);
    return (
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundImage: `url(${this.state.imageSrc})`,
          backgroundPosition: '50% 50%',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain'
        }}
      />
    );
  }

  renderNoPreviewAvailableIfNecessary() {
    if (this.props.isFolder) {
      return (
        <div
          style={{
            position: 'absolute',
            top: 'calc(50% - 47px)',
            left: 'calc(50% - 47px)'
          }}>
          <img style={{ height: '94px' }} src={'images/folder-gray.svg'} />
        </div>
      );
    }

    if (!this.state.imageDataReady || this.state.imageUnavailable || this.state.imageLoading) {
      return (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backgroundImage: `url(${this.state.imageSrc})`,
            backgroundPosition: '50% 50%',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain'
          }}
        />
      );
    }

    return null;
  }

  getShapes() {
    const shapes = [];
    _.each(this.props.comments, (shape) => {
      if (this.props.isMultiPage) {
        const temporalPos = AnnotationUtil.getTemporalLocation(shape);
        if (!temporalPos || this.state.currentPage !== AnnotationUtil.getVideOffsetInMilliseconds(shape)) {
          return;
        }
      }
      shapes.push(shape);
    });
    return shapes;
  }

  renderDrawingCanvas() {
    const {
      isMultiPage,
      editMode,
      pdfPreviewOption,
      comments,
      enableComments,
      asset,
      selectedAsset,
      collection,
      cropMode,
      isExpandedMode,
      metadataVisibiity,
      isAIData,
      ASSET_SCALE_INFO
    } = this.props;

    const { useNewImagePreview, imageSrc, imageUnavailable } = this.state;

    const numPages =
      isMultiPage && this.props.pdfPreviewOption === PdfPreviewOption.CONTINUOUS ? this.state.numPages : 1;

    if (this.props.isFolder) {
      return;
    }
    if (useNewImagePreview) {
      if (imageUnavailable) {
        return null;
      }
      const isAiView = ViewUtil.isDetailsBlockViewActive() ? (isAIData ? true : false) : false; //true; //ViewUtil.isAssetAIViewActive();
      const Component = isAiView ? AIData : enableComments ? ScaleableAssetViewWithComments : ScaleableAssetView;
      return (
        <Component
          asset={selectedAsset}
          collection={collection}
          src={imageSrc}
          width={this.state.imagePreviewResolutionX}
          height={this.state.imagePreviewResolutionY}
          comments={enableComments && this.props[SHOW_COMMENTS] ? comments : []}
          onScaleChange={this.onScaleChange}
          assetScaleInfo = {ASSET_SCALE_INFO}
          currentPage={this.currentPage}
          setLoading={() => this.setState({ imageLoading: false })}
          loading={this.state.imageLoading}
          cropMode={cropMode}
          isExpandedMode={isExpandedMode}
          metadataVisibiity={metadataVisibiity}
          isFullScreen={this.context.getTopBarVisibility() == Visibility.HIDDEN}
        />
      );
    }

    const drawingCanvas = (
      <DrawingCanvas
        ref="drawingCanvas"
        imageSrc={this.state.imageSrc}
        isUnavailable={!this.state.imageDataReady || this.state.imageUnavailable || this.state.imageLoading}
        width={this.state.imagePreviewResolutionX}
        height={this.state.imagePreviewResolutionY}
        isContinuousPage={pdfPreviewOption === PdfPreviewOption.CONTINUOUS}
        currentPage={this.currentPage}
        numPages={numPages}
        updatePage={this.updatePage}
        shapeDrawingStart={this.props.shapeDrawingStart}
        shapeDrawingStop={this.props.shapeDrawingStop}
        onTextInputHide={this.props.onTextInputHide}
        onRender={this.props.onRender}
        onClickThroughShape={this.props.onClickThroughShape}
        shapes={this.getShapes()}
        addingShape={this.props.addingShape}
        onShapeCreated={this.props.onShapeCreated}
        onShapeDeleted={this.props.onShapeDeleted}
        onShapeDrag={this.props.onShapeDrag}
        onShapeDragEnd={this.props.onShapeDragEnd}
        onCropResizeStop={this.props.onCropResizeStop}
      />
    );

    if (!(isMultiPage && ViewUtil.isAssetTabActive())) {
      return drawingCanvas;
    }

    const style = editMode ? { visibility: 'hidden' } : {};

    return (
      <div ref="drawingCanvasContainer" className="pdf-container">
        {drawingCanvas}
        <div className={`controls ${this.state.controlFocus ? 'focus' : ''}`} style={style}>
          <div className={pdfPreviewOption === PdfPreviewOption.PAGE_BY_PAGE ? '' : 'hidden'}>
            <button disabled={this.currentPage === 1} onClick={() => this.updatePage(this.currentPage - 1, true)}>
              <span />
            </button>
            <button
              disabled={this.currentPage === this.state.numPages}
              onClick={() => this.updatePage(this.currentPage + 1, true)}>
              <span />
            </button>
          </div>
          <div>
            <input
              ref={(node) => (this._input = node)}
              value={this.state.currentPage}
              onChange={this.onPageChange}
              onKeyUp={this.onKeyUp}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
            />
            <span />
            <button onClick={() => this.updatePage(this.state.numPages, true)}>{this.state.numPages}</button>
          </div>
        </div>
      </div>
    );
  }

  renderScreenToggleButton() {
    const { onFullScreenToggle, isExpandedMode, isCompareMode } = this.props;
    if (isCompareMode) {
      return null;
    }
    if (ViewUtil.isDirectLinkToCollection()) {
      const isFullscreen = this.context.getTopBarVisibility() === Visibility.HIDDEN;
      return (
        <Div
          className="screen-toggle"
          onClick={isFullscreen ? this.onExitFullScreenClick : this.handleExpandClick}
          title={isFullscreen ? t('EXIT_FULL_SCREEN') : t('VIEW_FULL_SCREEN')}
          style={{ ...styles.iconArrowExpandColored }}>
          <i
            className={classNames('fa', { 'fa-arrows-alt': !isFullscreen, 'fa-compress': isFullscreen })}
            aria-hidden="true"
          />
        </Div>
      );
    }

    if (isExpandedMode) {
      const isFullscreen = this.context.getTopBarVisibility() === Visibility.HIDDEN;
      // const isFullscreen = screenfull.isFullscreen;
      return (
        <Div
          className="screen-toggle"
          onClick={isFullscreen ? this.onExitFullScreenClick : this.handleExpandClick}
          title={isFullscreen ? t('EXIT_FULL_SCREEN') : t('VIEW_FULL_SCREEN')}
          style={{ ...styles.iconArrowExpandColored }}>
          <i
            className={classNames('fa', {
              'fa-arrows-alt': !isFullscreen,
              'fa-compress': isFullscreen
            })}
            aria-hidden="true"
          />
        </Div>
      );
    }

    if (onFullScreenToggle) {
      return (
        <Div
          className="screen-toggle"
          onClick={this.handleExpandClick}
          title={t('VIEW_EXPANDED_SCREEN')}
          style={{ ...styles.iconArrowExpandColored }}>
          <i className="fa fa-expand" aria-hidden="true" />
        </Div>
      );
    }

    return (
      <Div
        className="screen-toggle"
        onClick={screenfull.isFullscreen ? this.onExitFullScreenClick : this.handleExpandClick}
        title={screenfull.isFullscreen ? t('EXIT_FULL_SCREEN') : t('VIEW_FULL_SCREEN')}
        style={{ ...styles.iconArrowExpandColored }}>
        <i
          className={classNames('fa', {
            'fa-arrows-alt': !screenfull.isFullscreen,
            'fa-compress': screenfull.isFullscreen
          })}
          aria-hidden="true"
        />
      </Div>
    );
  }

  onScaleChange = (scale, firstRun, maxMinScale = null) => {
    const scaleInfo = { ...this.props[ASSET_SCALE_INFO], scale };
    if (firstRun) {
      scaleInfo.baseline = scale;
      if (maxMinScale) {
        scaleInfo.range = maxMinScale;
      } else {
        scaleInfo.range = {
          min: scale / 2 > 1 ? 1 : scale / 2,
          max: scale * 20
        };
      }
    }

    if (this.shouldRenderBottomBar()) {
      Store.put(ASSET_SCALE_INFO, scaleInfo);
    }
  };

  onImageLoaded = () => {
    this.setState({
      loading: true
    });
  };

  shouldRenderBottomBar = () => {
    const { comments, enableComments, isMultiPage, isExpandedMode } = this.props;

    if (isExpandedMode) {
      return false;
    }

    const { imageUnavailable } = this.state;
    if (imageUnavailable) {
      return false;
    }

    return (
      (enableComments && comments && comments.length) ||
      (ViewUtil.isCollectionTabActive() && isMultiPage) ||
      this.state.useNewImagePreview
    );
  };

  renderBottomBar = () => {
    const { scale, baseline, range } = this.props[ASSET_SCALE_INFO];
    const { isMultiPage, enableComments, renderDownloadButton, isCompareMode } = this.props;
    const { useNewImagePreview } = this.state;

    if (!this.shouldRenderBottomBar()) {
      return null;
    }

    if (isCompareMode) return;

    const width = screenfull.isFullscreen || isMobile() ? window.innerWidth : window.innerWidth - 370;
    return (
      <Row center="H" align="SPREAD" className="bottom-bar" style={{ width }}>
        <div />
        {isMultiPage || useNewImagePreview ? (
          <Center className="flex-row align-center">
            {isMultiPage ? (
              <MultipageControls
                current={this.state.currentPage}
                total={this.state.numPages}
                onChange={this.updatePage}
              />
            ) : null}
            {isMultiPage && useNewImagePreview ? <div style={{ width: 40 }} /> : null}
            {useNewImagePreview ? (
              <Row center="H">
                <SmartRangeInput
                  value={scale}
                  baseline={baseline}
                  min={range.min}
                  max={range.max}
                  onChange={this.onScaleChange}
                />
              </Row>
            ) : null}
          </Center>
        ) : null}
        {enableComments ? (
          <div className="showCommentsToggle showCommentButton" onClick={this.toggleShowComments}>
            <img
              src={
                this.props[SHOW_COMMENTS]
                  ? ThemeSetup.getImageURL('checkmark_red.svg')
                  : ThemeSetup.getImageURL('checkmark_unchecked.svg')
              }
              style={styles.checkbox}
            />
            <span style={styles.controlButtonText}>{t('SHOW_COMMENT')}</span>
          </div>
        ) : null}
        {renderDownloadButton()}
      </Row>
    );
  };

  render() {
    const { imageNormalFits, imageUnavailable, imageDataReady, imageLoading, useNewImagePreview } = this.state;

    if (!imageUnavailable) {
      this.state.imageSrc = ThumbnailUtil.createImagePreviewUrl(this.props.selectedAsset, this.currentPage);
    }

    let imageContainerStyle =
      this.props.isMultiPage || (useNewImagePreview && imageUnavailable) ? {} : { display: 'none' };
    if (!imageLoading && imageDataReady) {
      imageContainerStyle =
        imageNormalFits && !useNewImagePreview
          ? {
            ...styles.imagePreview,
            height: parseInt(this.state.imagePreviewResolutionY, 10),
            width: parseInt(this.state.imagePreviewResolutionX, 10),
            marginLeft: -(this.state.imagePreviewResolutionX / 2),
            marginTop: -(this.state.imagePreviewResolutionY / 2)
          }
          : {
            position: 'absolute',
            width: '100%',
            height: this.shouldRenderBottomBar() ? 'calc(100% - 40px)' : '100%'
          };
    }

    if (this.props.selectedAsset.getName().includes('.pdf')) {
      imageContainerStyle = {
        position: 'absolute',
        width: '100%',
        height: this.shouldRenderBottomBar() ? 'calc(100% - 40px)' : '100%'
      }
    }

    return (
      <div
        ref="wrapper"
        className={classNames({ multipage: this.props.isMultiPage })}
        name="AssetImagePreview"
        id="AssetImagePreview">
        {!imageLoading && !imageUnavailable && this.props.allowFullscreen && this.renderScreenToggleButton()}
        {imageLoading ? <Spinner large /> : null}
        <div
          ref="imageContainer"
          style={imageContainerStyle}
          onClick={(e) => {
            document.getElementById('replybox') ? document.getElementById('replybox').blur() : null;
          }}>
          {!this.props.selectedAsset.getName().includes('.pdf') ? (
            <img
              src={this.state.imageSrc}
              style={{ display: 'none' }}
              onLoad={this.handleImageLoad}
              onError={this.handleImageError}
              draggable="false"
            />
          ) : null}
          {this.renderDrawingCanvas()}
          {this.renderNoPreviewAvailableIfNecessary()}
        </div>
        {this.state.expandImageModal.show && (
          <ExpandImageModal
            onClose={this.handleExpandClick}
            assetURL={this.state.imageSrc}
            alt={this.props.selectedAsset._name}
          />
        )}
        {this.renderBottomBar()}
      </div>
    );
  }

  toggleShowComments = () => {
    Store.put(SHOW_COMMENTS, !this.props[SHOW_COMMENTS]);
  };

  setPreviewErrorImage = () => {
    this.setStateOnMountCheck({
      imageSrc: 'images/no_preview_available.png',
      imageLoading: false,
      imageUnavailable: true,
      imagePreviewResolutionX: 1980,
      imagePreviewResolutionY: 1080,
      imageNormalFits: false
    });
  }

  setIsComaparedImgAvailable = (isImgAvailable)=> {
    const {isComaparedImgAvailable, isLeftImg} = this.props;
    if(isComaparedImgAvailable) {
      isComaparedImgAvailable(isImgAvailable, isLeftImg);
    }
  }

  loadAssetInfo() {
    this._loadInfoJob = PreviewHelper.loadInfo(this.state.selectedAsset);
    this._loadInfoJob.promise.then(
      (previewInfo) => {
        if (!this.refs.wrapper) {
          return;
        }
        const selectedAsset: RepoItem = this.props.selectedAsset;
        const metadata_Map: HashMap = selectedAsset.getMetadataMap();
        const asset_width : number = parseInt(metadata_Map.get('FIPR_Image Width'));
        const asset_Height : number = parseInt(metadata_Map.get('FIPR_Image Height'));
        Store.put(ASSET_PREVIEW_INFO, previewInfo);
        const imagePreviewResolutionX = !previewInfo._width && asset_width ? asset_width : previewInfo._width;
        const imagePreviewResolutionY = !previewInfo._height && asset_Height ? asset_Height : previewInfo._height;
        if (imagePreviewResolutionX == 0 || imagePreviewResolutionY == 0) {
          //this.handleImageError('Zero image resolution received.');
          //return;
        }
        if(!previewInfo._type) {
         this.setIsComaparedImgAvailable(false)
         this.setPreviewErrorImage();
          return;
        }
        this.setIsComaparedImgAvailable(true)
        let imageNormalFits = this.checkIfImageFits(imagePreviewResolutionX, imagePreviewResolutionY);
        const __totalPages = previewInfo.totalPages ? previewInfo.totalPages : 0;
        this.setStateOnMountCheck({
          imagePreviewResolutionX,
          imagePreviewResolutionY,
          imageDataReady: true,
          numPages: __totalPages,
          imageNormalFits
        });
      },
      (error) => {
        this.handleImageError(error);
      }
    );
  }
}

const styles = {
  imagePreview: {
    display: 'inline-block',
    position: 'absolute',
    top: '50%',
    left: '50%'
  },
  iconArrowExpandColored: {
    height: '40px',
    width: '40px',
    position: 'absolute',
    top: '10px',
    right: '10px',
    cursor: 'pointer',
    zIndex: '11',
    fontSize: '20px',
    color: 'var(--primary-style-color)',
    textAlign: 'center',
    paddingTop: '8px'
  },
  checkbox: {
    width: '15px',
    height: '15px',
    marginRight: '3px'
  },
  controlButtonText: {
    margin: '0 5px 0 3px',
    fontSize: '14px',
    fontWeight: 400,
    verticalAlign: 'top',
    cursor: 'pointer',
    color: 'var(--primary-style-color)',
    userSelect: 'none',
    WebkitUserSelect: 'none'
  }
};
